import { loadEpicUiConfig } from '@/config/loadEpicUiConfig'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import React from 'react'
import BaseCallback, { CallbackConfig } from './Callback'

/**
 * Epic Sandbox specific callback implementation
 * Extends the base Callback component with Epic-specific authentication logic
 * Supports multi-client by dynamically selecting environment variables based on FHIR server
 */
const EpicSandboxCallback: React.FC = () => {
  const config: CallbackConfig = {
    retrieveAccessContext: async (authCode: string, state: string) => {
      const client = EpicAuthClient.getInstance()

      // Get the FHIR server from the session to determine which orgId to use
      if (!client.hasSession(state)) {
        throw new Error(
          'Invalid state parameter or session expired. ' +
            'Use one consistent origin for /launch and /epic-sandbox/callback (e.g. https://localhost:5173, not 127.0.0.1), ' +
            'and ensure client-config epicLandingCallback / Epic redirect URI match that exact URL.'
        )
      }

      const fhirServer = client.getFhirServerUrl(state)
      if (!fhirServer) {
        throw new Error('No FHIR server found in session')
      }

      // Get client-specific orgId based on FHIR server
      const orgId = client.getOrgId(state)
      if (!orgId) {
        throw new Error('No organization ID found in session')
      }

      const accessContext = await client.retrieveSmartOnFHIRAccessContext(authCode, state, orgId)
      const imdbOrgId = client.getImdbOrganizationId(state)?.trim()
      const pathOrgId = imdbOrgId || orgId?.trim()
      if (pathOrgId) {
        await loadEpicUiConfig(pathOrgId, fhirServer)
      } else {
        console.warn(
          '[EpicSandboxCallback] Skipping loadEpicUiConfig: no organization id on session after token exchange (need IMDB org or Epic org id in path, and FHIR base for lookup)'
        )
      }
      return {
        fhirPatientId: accessContext.fhirPatientId,
        userName: accessContext.userName,
        userLastName: accessContext.userLastName,
      }
    },
    loadingMessage: 'Requesting Epic access context...',
  }

  return <BaseCallback config={config} />
}

export default EpicSandboxCallback
